import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MerchShowcase from "@/components/MerchShowcase";
import { StreamGoalsWidget, LivePollWidget } from "@/components/StreamerToolsWidgets";
import { useWatchHeartbeat } from "@/hooks/useWatchHeartbeat";
import CommunitySection from "@/components/CommunitySection";
import ChannelLeaderboard from "@/components/ChannelLeaderboard";
import TipModal from "@/components/TipModal";
import ChannelSubscribeButton from "@/components/ChannelSubscribeButton";
import {
  Radio, Heart, Coins, ShieldCheck, Play, Palette, Save, X, Sparkles,
  Circle, MessageCircle, Trophy, Home, UserPlus, UserCheck,
} from "lucide-react";

const SOCIAL_META = {
  steam: { label: "Steam", color: "#66C0F4" },
  twitch: { label: "Twitch", color: "#9146FF" },
  kick: { label: "Kick", color: "#53FC18" },
  youtube: { label: "YouTube", color: "#FF0000" },
  tiktok: { label: "TikTok", color: "#25F4EE" },
  twitter: { label: "Twitter", color: "#1DA1F2" },
  instagram: { label: "Instagram", color: "#E4405F" },
  discord: { label: "Discord", color: "#5865F2" },
  reddit: { label: "Reddit", color: "#FF4500" },
  facebook: { label: "Facebook", color: "#1877F2" },
};

const BACKGROUNDS = [
  { key: "grid", label: "Neon Grid" },
  { key: "orbs", label: "Floating Orbs" },
  { key: "solid", label: "Solid Black" },
  { key: "cover", label: "Cover Photo" },
];

const PRESET_COLORS = ["#CCFF00", "#FF1053", "#FF5900", "#00F0FF", "#A855F7", "#F59E0B"];

export default function StreamerHome() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [channel, setChannel] = useState(null);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tipOpen, setTipOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const isOwner = user && channel && user.username === channel.username;

  useWatchHeartbeat(username, { enabled: !!user && !isOwner });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [c, p] = await Promise.all([
          api.get(`/channels/${username}`),
          api.get(`/profiles/${username}`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setChannel(c.data);
        setProfile(p.data);
        // Posts (public only shown here)
        if (user) {
          try {
            const posts = await api.get(`/posts?author=${username}&limit=6`);
            if (!cancelled) setPosts(posts.data.filter((x) => x.visibility === "public"));
          } catch { /* silent */ }
        }
      } catch (e) {
        toast.error("Channel not found");
      } finally { setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [username, user]);

  // Poll live status every 20s so the LIVE badge lights up shortly after
  // a streamer clicks Go Live without the viewer needing to refresh.
  useEffect(() => {
    if (!username) return;
    const id = setInterval(() => {
      api.get(`/channels/${username}`)
        .then(r => setChannel(prev => prev ? { ...prev, is_live: r.data.is_live, viewer_count: r.data.viewer_count, title: r.data.title, game_slug: r.data.game_slug } : r.data))
        .catch(() => {});
    }, 20000);
    return () => clearInterval(id);
  }, [username]);

  if (loading) return <div className="p-10 text-center text-zinc-500 sc-mono">loading home…</div>;
  if (!channel) return <div className="p-10 text-center text-zinc-400">Channel not found</div>;

  const theme = channel.home_theme || {};
  const accent = theme.accent_color || "#CCFF00";
  const bgStyle = theme.background_style || "grid";
  const socials = Object.entries(channel.social_links || {}).filter(([, v]) => v);
  const coverUrl = profile?.cover_path ? `${API}/files/${profile.cover_path}` : null;
  const showPosts = theme.show_posts !== false;
  const showSupporters = theme.show_top_supporters !== false;

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: "#0A0A0C" }}
      data-testid="streamer-home"
    >
      {/* Backdrop */}
      {bgStyle === "cover" && coverUrl && (
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      )}
      {(bgStyle === "grid" || bgStyle === "cover") && <div className="cyber-grid opacity-40" />}
      {bgStyle === "orbs" && (
        <>
          <div className="neon-orb" style={{ background: accent, width: 340, height: 340, top: -80, left: -80, opacity: 0.35 }} />
          <div className="neon-orb" style={{ background: "#FF1053", width: 280, height: 280, top: 200, right: -60, animationDelay: "-3s", opacity: 0.28 }} />
        </>
      )}
      <div className="crt-scanlines opacity-40" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-8">
        {/* ============ HEADER ============ */}
        <header className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="chip-neon flicker-slow" style={{ background: `${accent}22`, color: accent, borderColor: `${accent}66`, border: "1px solid" }}>
                <Home className="w-3 h-3" /> COMMUNITY HOME
              </span>
              {channel.is_live && (
                <span className="chip-neon pink flicker-slow">
                  <span className="rec-dot" style={{ width: 6, height: 6 }} /> LIVE NOW · {channel.viewer_count || 0} watching
                </span>
              )}
              {channel.verified_streamer && <span className="chip-neon volt"><ShieldCheck className="w-3 h-3" /> VERIFIED</span>}
            </div>
            <h1
              className="pixel-title text-5xl sm:text-6xl leading-none"
              style={{ color: accent }}
              data-testid="streamer-home-headline"
            >
              {theme.headline || `@${channel.username}`}
            </h1>
            <p className="text-zinc-300 text-lg mt-4 max-w-2xl leading-relaxed" data-testid="streamer-home-about">
              {theme.about || channel.description || "Welcome to my community home. This is where I hang out with viewers between streams."}
            </p>

            {theme.welcome_message && (
              <div className="mt-4 border-l-4 pl-4 py-2" style={{ borderColor: accent }}>
                <div className="text-sm text-zinc-300 italic">{theme.welcome_message}</div>
              </div>
            )}

            {/* CTA Row */}
            <div className="flex flex-wrap gap-3 mt-8">
              {channel.is_live ? (
                <Link
                  to={`/c/${channel.username}/live`}
                  className="text-base font-bold px-5 py-3 rounded flex items-center gap-2 anim-pulse-volt"
                  style={{ background: "#FF1053", color: "#fff" }}
                  data-testid="join-live-stream-btn"
                >
                  <Play className="w-4 h-4" fill="#fff" /> Join Live Stream
                </Link>
              ) : (
                <Link
                  to={`/c/${channel.username}/live`}
                  className="text-base font-bold px-5 py-3 rounded border flex items-center gap-2 hover:brightness-110"
                  style={{ borderColor: `${accent}66`, color: accent }}
                  data-testid="visit-stream-room-btn"
                >
                  <Radio className="w-4 h-4" /> Open Stream
                </Link>
              )}
              {!isOwner && (
                <button onClick={() => setTipOpen(true)} className="sc-btn-ghost flex items-center gap-2" data-testid="tip-btn">
                  <Coins className="w-4 h-4 text-[#F59E0B]" /> Tip
                </button>
              )}
              <ChannelSubscribeButton ownerUsername={channel.username} />
              <Link
                to={`/c/${channel.username}/leaderboards`}
                className="sc-btn-ghost text-sm flex items-center gap-2"
                data-testid="channel-leaderboards-link"
              >
                <Trophy className="w-4 h-4 text-[color:var(--sc-volt)]" /> Leaderboards
              </Link>
              {isOwner && (
                <button
                  onClick={() => setEditing(true)}
                  className="sc-btn-ghost flex items-center gap-2"
                  style={{ borderColor: `${accent}66`, color: accent }}
                  data-testid="customize-home-btn"
                >
                  <Palette className="w-4 h-4" /> Customize
                </button>
              )}
            </div>

            {socials.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-6" data-testid="home-socials">
                {socials.map(([k, v]) => {
                  const meta = SOCIAL_META[k];
                  if (!meta) return null;
                  const href = v.startsWith("http") ? v : `https://${v}`;
                  return (
                    <a key={k} href={href} target="_blank" rel="noreferrer noopener"
                       className="text-[10px] tracking-widest uppercase font-bold px-2.5 py-1.5 border rounded-sm hover:bg-white/5 transition"
                       style={{ borderColor: meta.color + "60", color: meta.color }}>
                      {meta.label}
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Avatar card */}
          <div className="sc-card p-6 text-center bracket-corners">
            <div className="w-28 h-28 rounded-full mx-auto overflow-hidden border-4"
                 style={{ borderColor: accent, boxShadow: `0 0 30px ${accent}55` }}>
              {profile?.avatar_path ? (
                <img src={`${API}/files/${profile.avatar_path}`} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#18181C] sc-heading text-4xl font-black" style={{ color: accent }}>
                  {channel.username.slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <div className="sc-heading text-xl font-black mt-3">@{channel.username}</div>
            <div className="text-xs text-zinc-500 mt-1">{channel.category}</div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              <div className="p-2 rounded bg-black/30">
                <div className="sc-label text-[9px]" style={{ color: accent }}>SUBS</div>
                <div className="sc-heading text-lg font-black">{channel.subscriber_count || 0}</div>
              </div>
              <div className="p-2 rounded bg-black/30">
                <div className="sc-label text-[9px]" style={{ color: accent }}>FOLLOWERS</div>
                <div className="sc-heading text-lg font-black">{channel.follower_count || 0}</div>
              </div>
            </div>
            <Link to={`/u/${channel.username}`} className="text-[10px] sc-mono text-zinc-500 hover:text-white mt-4 inline-flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> View social profile →
            </Link>
          </div>
        </header>

        {/* ============ FULL COMMUNITY HUB (inlined; former /c/:user/community page) ============ */}
        <CommunitySection channel={channel} />

        {/* ============ EXTERNAL MERCH SHOWCASE ============ */}
        <MerchShowcase channel={channel} />
        <StreamGoalsWidget channel={channel.username} />
        <LivePollWidget channel={channel.username} />

        {/* ============ CHANNEL LEADERBOARDS (streamer-configurable) ============ */}
        {showSupporters && <ChannelLeaderboard channelUsername={channel.username} />}

        {/* ============ RECENT POSTS ============ */}
        {showPosts && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="sc-label" style={{ color: accent }}>◈ RECENT POSTS</div>
                <h2 className="sc-heading text-3xl font-black">From @{channel.username}</h2>
              </div>
              <Link to={`/u/${channel.username}`} className="sc-btn-ghost text-xs">See all →</Link>
            </div>
            {posts.length === 0 ? (
              <div className="sc-card p-8 text-center text-sm text-zinc-500">
                {isOwner ? "You haven't shared any public posts yet." : "No public posts yet."}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {posts.map((p) => (
                  <Link to={`/u/${channel.username}`} key={p.id} className="sc-card p-4 hover:brightness-110 transition">
                    <div className="text-xs text-zinc-500 sc-mono mb-2">{p.created_at?.slice(0, 10)}</div>
                    <div className="text-sm text-zinc-200 line-clamp-4">{p.body || "(media only)"}</div>
                    {(p.media || []).length > 0 && (
                      <div className="mt-2 grid grid-cols-3 gap-1">
                        {(p.media || []).slice(0, 3).map((m, i) => (
                          <div key={i} className="aspect-square rounded overflow-hidden bg-black/40">
                            {(m.content_type || "").startsWith("image/") && (
                              <img src={`${API}/files/${m.path}`} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] text-zinc-500 sc-mono mt-2 flex items-center gap-3">
                      <span>♥ {p.total_reactions}</span>
                      <span>💬 {p.comment_count}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ============ CTA FOOTER ============ */}
        <div className="sc-card p-8 text-center relative overflow-hidden" style={{ borderColor: `${accent}60` }} data-testid="home-cta-footer">
          <Trophy className="w-8 h-8 mx-auto mb-3" style={{ color: accent }} />
          <div className="sc-heading text-2xl font-black">Get into the room.</div>
          <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
            {channel.is_live
              ? "The stream is live — chat, hang out, jump into the game."
              : "The next stream is coming. Follow, subscribe, or tip to support the community."}
          </p>
          {!isOwner ? (
            <div className="flex justify-center gap-3 mt-5 flex-wrap">
              <button
                onClick={() => setTipOpen(true)}
                className="sc-btn-ghost text-sm flex items-center gap-2"
                data-testid="cta-tip-btn"
              >
                <Coins className="w-4 h-4 text-[#F59E0B]" /> Tip
              </button>
              <FollowActionButton channel={channel} />
              <ChannelSubscribeButton ownerUsername={channel.username} />
            </div>
          ) : (
            <div className="flex justify-center gap-3 mt-5 flex-wrap">
              <Link to={`/c/${channel.username}/live`} className="sc-btn text-sm flex items-center gap-2" data-testid="cta-open-stream-owner">
                <Play className="w-4 h-4" /> Open Stream
              </Link>
              <Link to="/dashboard/channel" className="sc-btn-ghost text-sm flex items-center gap-2" data-testid="cta-edit-channel">
                Edit channel
              </Link>
            </div>
          )}
        </div>
      </div>

      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} streamerUsername={channel.username} />
      {editing && (
        <CustomizeModal
          initial={theme}
          onClose={() => setEditing(false)}
          onSaved={(t) => { setChannel({ ...channel, home_theme: { ...theme, ...t } }); setEditing(false); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Follow / Unfollow button — used in the CTA footer
// ============================================================
function FollowActionButton({ channel }) {
  const { user } = useAuth();
  const [state, setState] = useState({ following: false, is_self: false, loaded: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !channel?.username) {
      setState({ following: false, is_self: false, loaded: true });
      return;
    }
    api.get(`/users/${channel.username}/follow-state`)
      .then((r) => setState({ ...r.data, loaded: true }))
      .catch(() => setState({ following: false, is_self: false, loaded: true }));
  }, [channel?.username, user?.username]);

  const toggle = async () => {
    if (!user) return toast.error("Log in to follow this channel");
    if (state.is_self) return;
    setBusy(true);
    try {
      if (state.following) {
        await api.delete(`/users/${channel.username}/follow`);
        setState((s) => ({ ...s, following: false }));
        toast.success(`Unfollowed ${channel.username}`);
      } else {
        await api.post(`/users/${channel.username}/follow`);
        setState((s) => ({ ...s, following: true }));
        toast.success(`Following ${channel.username}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Follow failed");
    } finally { setBusy(false); }
  };

  if (state.is_self) return null;

  return (
    <button
      onClick={toggle}
      disabled={busy || !state.loaded}
      className={`text-sm flex items-center gap-2 px-4 py-2 rounded-md border transition disabled:opacity-60 ${
        state.following
          ? "border-white/20 text-zinc-300 hover:border-[color:var(--sc-pink)] hover:text-[color:var(--sc-pink)]"
          : "border-[color:var(--sc-cyan)] text-[color:var(--sc-cyan)] hover:bg-[color:var(--sc-cyan)]/10"
      }`}
      data-testid={state.following ? "cta-unfollow-btn" : "cta-follow-btn"}
    >
      {state.following ? (
        <><UserCheck className="w-4 h-4" /> Following</>
      ) : (
        <><UserPlus className="w-4 h-4" /> Follow</>
      )}
    </button>
  );
}

// ============================================================
// Customization modal (owner only)
// ============================================================
function CustomizeModal({ initial, onClose, onSaved }) {
  const [headline, setHeadline] = useState(initial.headline || "");
  const [about, setAbout] = useState(initial.about || "");
  const [welcome, setWelcome] = useState(initial.welcome_message || "");
  const [color, setColor] = useState(initial.accent_color || "#CCFF00");
  const [bg, setBg] = useState(initial.background_style || "grid");
  const [showPosts, setShowPosts] = useState(initial.show_posts !== false);
  const [showSup, setShowSup] = useState(initial.show_top_supporters !== false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        headline, about, welcome_message: welcome,
        accent_color: color, background_style: bg,
        show_posts: showPosts, show_top_supporters: showSup,
      };
      await api.patch("/channels/me/home", payload);
      toast.success("Home saved");
      onSaved(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(20px)" }} onClick={onClose}>
      <div className="sc-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customize-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="sc-heading text-xl font-black flex items-center gap-2"><Palette className="w-5 h-5 text-[color:var(--sc-volt)]" /> Customize your home</div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="sc-label block mb-2">Headline</label>
            <input value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={120} className="sc-input" placeholder="e.g. @yourname's cyberpunk arcade" data-testid="home-headline-input" />
          </div>
          <div>
            <label className="sc-label block mb-2">About</label>
            <textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={3} maxLength={2000} className="sc-input resize-none" placeholder="Tell viewers about your community — what you play, when you stream, house rules." data-testid="home-about-input" />
          </div>
          <div>
            <label className="sc-label block mb-2">Welcome message (blockquote)</label>
            <input value={welcome} onChange={(e) => setWelcome(e.target.value)} maxLength={400} className="sc-input" placeholder="e.g. Weekly poker night Fridays at 9pm ET" data-testid="home-welcome-input" />
          </div>
          <div>
            <label className="sc-label block mb-2">Accent color</label>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} className={`w-9 h-9 rounded-full border-2 ${color === c ? "ring-2 ring-white" : ""}`} style={{ background: c, borderColor: color === c ? "white" : "transparent" }} data-testid={`home-color-${c.slice(1)}`} aria-label={`Color ${c}`} />
              ))}
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-9 h-9 rounded cursor-pointer" data-testid="home-color-picker" />
              <span className="sc-mono text-xs text-zinc-500">{color}</span>
            </div>
          </div>
          <div>
            <label className="sc-label block mb-2">Background style</label>
            <div className="grid grid-cols-2 gap-2">
              {BACKGROUNDS.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setBg(b.key)}
                  className={`p-3 rounded border text-xs ${bg === b.key ? "bg-[color:var(--sc-volt)]/15 border-[color:var(--sc-volt)] text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                  data-testid={`home-bg-${b.key}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={showSup} onChange={(e) => setShowSup(e.target.checked)} data-testid="home-toggle-supporters" />
              Show top supporters section
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={showPosts} onChange={(e) => setShowPosts(e.target.checked)} data-testid="home-toggle-posts" />
              Show recent posts
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="sc-btn-ghost text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="sc-btn text-xs flex items-center gap-1.5" data-testid="home-save">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
